import React, { useState, forwardRef, useEffect, useMemo } from 'react';

import TableContainer from '@hud/web-components/Container/TableContainer';
import { DefaultTable } from '@hud/web-components/Table/Table';
import { Search } from '@hud/web-components/Search/Search';
import { ItemCounter } from '@hud/web-components/ItemCounter/ItemCounter';
import { ErrorBreakdown } from '@hud/portal-dtos/webapp-function.dto';
import Pagination from '@hud/web-components/Pagination/Pagination';
import {
  PaginationState,
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import LongContentPopover from '@hud/web-components/LongContentPopover/LongContentPopover';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import metricProgressCellRenderer from '../metricProgressCellRenderer/metricProgressCellRenderer';
import { DEFAULT_INNER_TABLE_PAGE_SIZE } from '../../consts';
import useAnalytics from '../../hooks/useAnalytics';
import renderMetric, {
  formatMetric,
} from '../metricCellRenderer/metricCellRenderer';
import useEnvironment from '../../hooks/useEnvironment';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { formatDateTime } from '@hud/web-components/utils';
import HoverPopover from '@hud/web-components/HoverPopover/HoverPopover';

export interface FunctionDataForErrors {
  id: number;
  name: string;
  firstSeenAt: Date;
}

export interface FunctionErrorsTableProps {
  errors?: ErrorBreakdown;
  function?: FunctionDataForErrors;
  loading: boolean;
  isKafkaTopic?: boolean;
}

const columnHelper = createColumnHelper<ErrorBreakdown[0]>();

const FunctionErrorsTable = forwardRef<
  HTMLDivElement,
  FunctionErrorsTableProps
>(({ errors, function: func, loading, isKafkaTopic }, ref) => {
  const analytics = useAnalytics();
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'normalizedCount.value',
      desc: true,
    },
  ]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_INNER_TABLE_PAGE_SIZE,
  });
  const { environment } = useEnvironment();

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor('name', {
        header: 'Error',
        cell: (cell) => (
          <LongContentPopover>{cell.getValue()}</LongContentPopover>
        ),
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.accessor('metrics.count.value', {
        id: 'count.value',
        header: 'Count',
        cell: renderMetric('number'),
        size: 100,
        sortingFn: (a, b) =>
          a.original.metrics.count.value - b.original.metrics.count.value,
        meta: {
          fitFormatter: formatMetric('number'),
          rightAlign: true,
        },
      }),
      columnHelper.accessor('metrics.normalizedCount.value', {
        id: 'normalizedCount.value',
        header: 'Count/d',
        cell: renderMetric('number'),
        size: 100,
        sortingFn: (a, b) =>
          a.original.metrics.normalizedCount.value -
          b.original.metrics.normalizedCount.value,
        meta: {
          fitFormatter: formatMetric('number'),
          rightAlign: true,
        },
      }),
      columnHelper.accessor('metrics.distribution.value', {
        id: 'distribution.value',
        header: 'Distribution',
        cell: metricProgressCellRenderer,
        size: 100,
      }),
      columnHelper.accessor('lastSeenAt', {
        header: 'Last Seen',
        cell: (cell) => {
          const value = cell.getValue();
          if (!value) return 'N/A';
          return (
            <HoverPopover body={formatDateTime(value, false)}>
              {formatDateTime(value, true)}
            </HoverPopover>
          );
        },
        size: 200,
        meta: {
          fitFormatter: (cell) =>
            cell.getValue()
              ? formatDateTime(cell.getValue() as Date, true)
              : 'N/A',
        },
      }),
    ];
    return cols.filter((col) => !isKafkaTopic || col.accessorKey !== 'lastSeenAt');
  }, []);

  const table = useReactTable({
    data: errors ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    columnResizeMode: 'onChange',
    enableSortingRemoval: false,
    state: {
      pagination,
      sorting,
      globalFilter,
    },
    defaultColumn: {
      minSize: 80,
    },
    onSortingChange: setSorting,
  });

  useEffect(() => {
    if (table.getState().sorting.length === 0 || !func) return;
    analytics.capture('function_errors_sorted', {
      function_id: func.id,
      function_name: func.name,
      column_name: sorting[0]?.id ?? '',
      column_order: sorting[0]?.desc ? 'desc' : 'asc',
      environment: environment?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  return (
    <TableContainer.Container ref={ref} title={`Errors`} id="function-errors">
      <TableContainer.Header>
        <Search
          inline
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
        {loading ? (
          <Skeleton width={100} />
        ) : errors ? (
          <ItemCounter
            total={errors.length}
            filtered={table.getPrePaginationRowModel().rows.length}
          />
        ) : null}
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable
          table={table}
          label="Errors Table"
          loading={loading}
          loadingRows={5}
        />
      </HorizontalContainer>
      <TableContainer.Footer>
        <Pagination
          pages={loading ? 0 : table.getPageCount()}
          currentPage={
            loading || table.getPageCount() === 0
              ? 0
              : table.getState().pagination.pageIndex + 1
          }
          onPageChange={(page) => table.setPageIndex(page - 1)}
          loading={loading}
        />
      </TableContainer.Footer>
    </TableContainer.Container>
  );
});

export default FunctionErrorsTable;
